const PRICE_NEGOTIATION_CACHE_PREFIX =
  "loadevo.shipment-price-negotiation.v1";

const PRICE_NEGOTIATION_CACHE_EVENT =
  "loadevo:shipment-price-negotiation-cache";

const PRICE_NEGOTIATION_CACHE_MAX_AGE =
  24 *
  60 *
  60 *
  1000;

const memoryCache =
  new Map();

function isBrowser() {
  return (
    typeof window !==
    "undefined"
  );
}

function getAuthToken() {
  if (
    !isBrowser()
  ) {
    return "";
  }

  return String(
    localStorage.getItem(
      "token"
    ) ||
      sessionStorage.getItem(
        "token"
      ) ||
      localStorage.getItem(
        "authToken"
      ) ||
      sessionStorage.getItem(
        "authToken"
      ) ||
      localStorage.getItem(
        "accessToken"
      ) ||
      sessionStorage.getItem(
        "accessToken"
      ) ||
      ""
  )
    .trim()
    .replace(
      /^Bearer\s+/i,
      ""
    )
    .replace(
      /^"|"$/g,
      ""
    );
}

function decodeJwtPayload(
  token
) {
  try {
    const encodedPayload =
      String(
        token ||
          ""
      ).split(
        "."
      )[1];

    if (
      !encodedPayload
    ) {
      return null;
    }

    const normalizedPayload =
      encodedPayload
        .replace(
          /-/g,
          "+"
        )
        .replace(
          /_/g,
          "/"
        );

    const paddedPayload =
      normalizedPayload.padEnd(
        Math.ceil(
          normalizedPayload.length /
            4
        ) *
          4,
        "="
      );

    return JSON.parse(
      window.atob(
        paddedPayload
      )
    );
  } catch {
    return null;
  }
}

function hashString(
  value
) {
  let hash =
    2166136261;

  const normalizedValue =
    String(
      value ||
        ""
    );

  for (
    let index =
      0;
    index <
    normalizedValue.length;
    index +=
      1
  ) {
    hash ^=
      normalizedValue.charCodeAt(
        index
      );

    hash =
      Math.imul(
        hash,
        16777619
      );
  }

  return (
    hash >>>
    0
  ).toString(
    36
  );
}

function getAuthScope() {
  const token =
    getAuthToken();

  if (
    !token
  ) {
    return "anonymous";
  }

  const payload =
    decodeJwtPayload(
      token
    ) ||
    {};

  const companyId =
    payload.companyId ??
    payload.company_id ??
    payload.cid ??
    payload.carrierCompanyId ??
    payload.carrier_company_id;

  const userId =
    payload.userId ??
    payload.user_id ??
    payload.sub;

  if (
    companyId !==
      null &&
    companyId !==
      undefined &&
    String(
      companyId
    ).trim()
  ) {
    return `company-${companyId}`;
  }

  if (
    userId !==
      null &&
    userId !==
      undefined &&
    String(
      userId
    ).trim()
  ) {
    return `user-${userId}`;
  }

  return `session-${hashString(
    token
  )}`;
}

function getPriceNegotiationCacheKey(
  requestId
) {
  return [
    PRICE_NEGOTIATION_CACHE_PREFIX,
    getAuthScope(),
    String(
      requestId
    ),
  ].join(
    ":"
  );
}

function parseCacheEntry(
  rawEntry
) {
  if (
    !rawEntry
  ) {
    return null;
  }

  try {
    const parsedEntry =
      typeof rawEntry ===
      "string"
        ? JSON.parse(
            rawEntry
          )
        : rawEntry;

    const savedAt =
      Number(
        parsedEntry
          ?.savedAt
      );

    const data =
      parsedEntry
        ?.data;

    if (
      !Number.isFinite(
        savedAt
      ) ||
      !data ||
      typeof data !==
        "object"
    ) {
      return null;
    }

    const age =
      Date.now() -
      savedAt;

    if (
      age <
        0 ||
      age >
        PRICE_NEGOTIATION_CACHE_MAX_AGE
    ) {
      return null;
    }

    return {
      savedAt,
      age,
      data,
    };
  } catch {
    return null;
  }
}

export function readShipmentPriceNegotiationCache(
  requestId
) {
  if (
    !requestId
  ) {
    return null;
  }

  const cacheKey =
    getPriceNegotiationCacheKey(
      requestId
    );

  const memoryEntry =
    parseCacheEntry(
      memoryCache.get(
        cacheKey
      )
    );

  if (
    memoryEntry
  ) {
    return memoryEntry;
  }

  if (
    !isBrowser()
  ) {
    return null;
  }

  try {
    const storedEntry =
      parseCacheEntry(
        localStorage.getItem(
          cacheKey
        )
      );

    if (
      !storedEntry
    ) {
      localStorage.removeItem(
        cacheKey
      );

      return null;
    }

    memoryCache.set(
      cacheKey,
      storedEntry
    );

    return storedEntry;
  } catch {
    return null;
  }
}

export function writeShipmentPriceNegotiationCache(
  requestId,
  data
) {
  if (
    !requestId ||
    !data ||
    typeof data !==
      "object"
  ) {
    return;
  }

  const cacheKey =
    getPriceNegotiationCacheKey(
      requestId
    );

  const cacheEntry = {
    savedAt:
      Date.now(),

    data,
  };

  memoryCache.set(
    cacheKey,
    cacheEntry
  );

  if (
    !isBrowser()
  ) {
    return;
  }

  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify(
        cacheEntry
      )
    );
  } catch (
    storageError
  ) {
    console.warn(
      "[price-negotiation-cache] Could not persist cache:",
      storageError
    );
  }

  window.dispatchEvent(
    new CustomEvent(
      PRICE_NEGOTIATION_CACHE_EVENT,
      {
        detail: {
          cacheKey,
          entry:
            cacheEntry,
        },
      }
    )
  );
}

export function removeShipmentPriceNegotiationCache(
  requestId
) {
  if (
    !requestId
  ) {
    return;
  }

  const cacheKey =
    getPriceNegotiationCacheKey(
      requestId
    );

  memoryCache.delete(
    cacheKey
  );

  if (
    !isBrowser()
  ) {
    return;
  }

  try {
    localStorage.removeItem(
      cacheKey
    );
  } catch {}
}

export function subscribeShipmentPriceNegotiationCache(
  requestId,
  listener
) {
  if (
    !isBrowser() ||
    !requestId ||
    typeof listener !==
      "function"
  ) {
    return () => {};
  }

  const cacheKey =
    getPriceNegotiationCacheKey(
      requestId
    );

  const handleStorage =
    (
      event
    ) => {
      if (
        event.key !==
        cacheKey ||
        !event.newValue
      ) {
        return;
      }

      const entry =
        parseCacheEntry(
          event.newValue
        );

      if (
        entry
      ) {
        memoryCache.set(
          cacheKey,
          entry
        );

        listener(
          entry
        );
      }
    };

  const handleLocalCache =
    (
      event
    ) => {
      if (
        event.detail
          ?.cacheKey !==
        cacheKey
      ) {
        return;
      }

      const entry =
        parseCacheEntry(
          event.detail
            ?.entry
        );

      if (
        entry
      ) {
        listener(
          entry
        );
      }
    };

  window.addEventListener(
    "storage",
    handleStorage
  );

  window.addEventListener(
    PRICE_NEGOTIATION_CACHE_EVENT,
    handleLocalCache
  );

  return () => {
    window.removeEventListener(
      "storage",
      handleStorage
    );

    window.removeEventListener(
      PRICE_NEGOTIATION_CACHE_EVENT,
      handleLocalCache
    );
  };
}